import React from 'react';
import { Link } from 'react-router-dom';
import { useEmail } from '../hooks/useConfig';

function Support() {
  const { email: supportEmail, loading } = useEmail('support');

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Support & Help Center
          </h1>
          <p className="text-xl text-gray-600">
            Need assistance? We're here to help!
          </p>
        </div>

        {/* Contact Support Card */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex items-center mb-6">
            <svg className="h-8 w-8 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900">Contact Support</h2>
          </div>

          <p className="text-gray-600 mb-6">
            Have a question or need technical assistance? Our support team is ready to help.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="text-gray-700 font-medium">Email Support:</span>
            </div>
            {loading ? (
              <div className="text-2xl font-semibold text-gray-400">Loading...</div>
            ) : (
              <>
                <a
                  href={`mailto:${supportEmail}?subject=Snaptura Support Request`}
                  className="text-2xl font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                >
                  {supportEmail}
                </a>
                <p className="text-sm text-gray-500 mt-3">
                  We typically respond within 24 hours on business days
                </p>
              </>
            )}
          </div>
        </div>

        {/* Quick Help Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Help</h2>

          <div className="space-y-6">
            {/* Getting Started */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Getting Started</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Create an account and verify your email</li>
                <li>• Generate an API key from the <Link to="/api-keys" className="text-blue-600 hover:underline">API Keys</Link> page</li>
                <li>• Make your first API request using our documentation</li>
                <li>• Monitor your usage on the <Link to="/dashboard" className="text-blue-600 hover:underline">Dashboard</Link></li>
              </ul>
            </div>

            {/* API Usage */}
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">API Usage</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Include your API key in the <code className="bg-gray-100 px-2 py-1 rounded">X-API-Key</code> header</li>
                <li>• Use <code className="bg-gray-100 px-2 py-1 rounded">POST /api/screenshot</code> for website screenshots</li>
                <li>• Use <code className="bg-gray-100 px-2 py-1 rounded">POST /api/pdf</code> for PDF generation</li>
                <li>• Check rate limits and quotas on your current plan</li>
              </ul>
            </div>

            {/* Billing */}
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Billing & Plans</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• View available plans on the <Link to="/pricing" className="text-blue-600 hover:underline">Pricing</Link> page</li>
                <li>• Upgrade or manage your subscription in <Link to="/billing" className="text-blue-600 hover:underline">Billing</Link></li>
                <li>• Free tier includes 10 requests per month</li>
                <li>• No credit card required for free tier</li>
              </ul>
            </div>

            {/* Troubleshooting */}
            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Common Issues</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• <strong>401 Unauthorized:</strong> Check your API key is correct and active</li>
                <li>• <strong>429 Rate Limited:</strong> You've exceeded your plan's quota</li>
                <li>• <strong>Screenshot timeout:</strong> Try using waitUntil: 'domcontentloaded' for faster results</li>
                <li>• <strong>Email not verified:</strong> Check spam folder and resend verification email</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Resources */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Resources</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 transition-colors">
              <h3 className="font-semibold text-gray-900 mb-2">Documentation</h3>
              <p className="text-gray-600 text-sm mb-3">
                Complete API reference and integration guides
              </p>
              <Link to="/docs" className="text-blue-600 hover:underline text-sm font-medium">
                View Docs →
              </Link>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
              <h3 className="font-semibold text-gray-900 mb-2">Status Page</h3>
              <p className="text-gray-600 text-sm mb-3">
                Check API status and uptime
              </p>
              <span className="text-gray-400 text-sm font-medium">
                Coming Soon →
              </span>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 transition-colors">
              <h3 className="font-semibold text-gray-900 mb-2">Privacy Policy</h3>
              <p className="text-gray-600 text-sm mb-3">
                How we handle your data
              </p>
              <Link to="/privacy" className="text-blue-600 hover:underline text-sm font-medium">
                Read Policy →
              </Link>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 transition-colors">
              <h3 className="font-semibold text-gray-900 mb-2">Terms of Service</h3>
              <p className="text-gray-600 text-sm mb-3">
                Usage terms and conditions
              </p>
              <Link to="/terms" className="text-blue-600 hover:underline text-sm font-medium">
                Read Terms →
              </Link>
            </div>
          </div>
        </div>

        {/* Back to Dashboard */}
        <div className="text-center mt-8">
          <Link
            to="/dashboard"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Support;
